import { apiClient } from './apiClient';
import { setDateTimePreferences } from '../shared/dateTimeFormatting';
import type { HourCycle } from '../shared/dateTimePreferences';

export type AuthMethod = 'sso' | 'magic_link';
export const AUTH_METHOD_STORAGE_KEY = 'nog_auth_method';

export function getStoredAuthMethod(): AuthMethod | null {
  try {
    const raw = localStorage.getItem(AUTH_METHOD_STORAGE_KEY);
    if (raw === 'sso' || raw === 'magic_link') {
      return raw;
    }
  } catch {
    // Ignore storage errors
  }
  return null;
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  phone?: string;
  tenantId: string | null;
  tenantName: string | null;
  roles: string[];
  permissions: string[];
  timezone?: string;
  dateTimeLocale?: string | null;
  hourCycle?: HourCycle | null;
}

export interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;
}

type PortalType = 'super-admin' | 'tenant-admin' | 'user';

class AuthService {
  private state: AuthState = {
    user: null,
    isLoading: true,
    isAuthenticated: false,
    token: null,
    refreshToken: null,
  };

  private portalType: PortalType = 'user';
  private tenantSlug: string | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<() => void>();
  private sessionRevision = 0;

  getSessionRevision(): number {
    return this.sessionRevision;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private publish(): void {
    setDateTimePreferences(this.state.user);
    this.listeners.forEach(listener => listener());
  }

  adoptUser(user: User, sessionRevision = this.sessionRevision): void {
    if (sessionRevision !== this.sessionRevision) return;
    this.state = { ...this.state, user: { ...user } };
    this.saveStateToStorage();
  }

  resetSession(): void {
    this.clearState();
  }

  async initialize(portalType: PortalType, tenantSlug?: string): Promise<void> {
    this.portalType = portalType;
    this.tenantSlug = tenantSlug || null;

    const storedState = this.loadStateFromStorage();
    
    if (storedState.token) {
      // Token but no user — need to validate
      this.state = {
        user: storedState.user ?? null,
        token: storedState.token ?? null,
        refreshToken: storedState.refreshToken ?? null,
        isAuthenticated: false,
        isLoading: true,
      };

      try {
        await this.validateAndRefreshToken(storedState.token);
        this.state.isLoading = false;
      } catch (err) {
        this.clearState();
      }
    } else {
      this.clearState();
    }

    this.saveStateToStorage();
    this.setupTokenRefresh();
  }

  getState(): AuthState {
    return { ...this.state };
  }

  adoptSessionTokens(token: string, refreshToken?: string | null): void {
    if (!token) {
      return;
    }

    const newSession = token !== this.state.token;
    if (newSession) this.sessionRevision += 1;
    this.state = {
      ...this.state,
      user: newSession ? null : this.state.user,
      token,
      refreshToken: refreshToken ?? this.state.refreshToken,
      isAuthenticated: true,
      isLoading: false,
    };

    this.saveStateToStorage();
  }

  async loginWithRedirect(): Promise<void> {
    const returnUrl = this.getReturnUrl();
    
    switch (this.portalType) {
      case 'super-admin':
        await this.loginSuperAdmin(returnUrl);
        break;
      case 'tenant-admin':
      case 'user':
        await this.loginTenantUser(returnUrl);
        break;
    }
  }

  async loginWithMagicLink(email: string): Promise<void> {
    const returnUrl = this.getReturnUrl();
    
    try {
      const response = await apiClient.post('/api/auth/magic-link', {
        email,
        returnUrl,
        tenantSlug: this.tenantSlug,
      }) as any;

      if (response.magicLinkUrl) {
        window.location.href = response.magicLinkUrl;
      }
    } catch (error) {
      console.error('Magic link request failed:', error);
      throw error;
    }
  }

  /**
   * Handle the OAuth callback. Returns the raw response for special cases
   * (e.g. multiple_tenants) that need further handling by the caller.
   */
  async handleCallback(code?: string): Promise<any> {
    try {
      let response: any;

      if (this.portalType === 'super-admin') {
        response = await apiClient.post('/api/auth/sso/callback', {
          code,
          redirectUri: `${window.location.origin}/login/complete`,
        }) as any;
      } else {
        response = await apiClient.post('/api/auth/tenant/callback', { 
          code,
          tenantSlug: this.tenantSlug,
          redirectUri: `${window.location.origin}/login/complete`,
        }) as any;
      }

      // Multi-tenant case: return response to caller for picker handling
      if (response.error === 'multiple_tenants' && response.tenants?.length > 0) {
        return response;
      }

      if (!response.success && !response.token) {
        throw new Error(response.error || 'Authentication failed');
      }

      const { token, refreshToken, user } = response;

      if (!token || !user) {
        throw new Error('Invalid authentication response — missing token or user');
      }

      this.state = {
        user,
        token,
        refreshToken,
        isAuthenticated: true,
        isLoading: false,
      };

      this.persistAuthMethod('sso');
      this.saveStateToStorage();
      return response;
    } catch (error) {
      console.error('Callback handling failed:', error);
      this.clearState();
      throw error;
    }
  }

  /**
   * Complete SSO login after the user selects a tenant from the multi-tenant picker.
   */
  async completeSsoTenantSelection(nonce: string, tenantSlug: string): Promise<void> {
    try {
      const response = await apiClient.post('/api/auth/sso/complete-tenant', {
        nonce,
        tenantSlug,
      }) as any;

      if (!response.success && !response.token) {
        throw new Error(response.error || 'Authentication failed');
      }

      const { token, refreshToken, user } = response;

      if (!token || !user) {
        throw new Error('Invalid authentication response — missing token or user');
      }

      this.state = {
        user,
        token,
        refreshToken,
        isAuthenticated: true,
        isLoading: false,
      };

      this.persistAuthMethod('sso');
      this.saveStateToStorage();
    } catch (error) {
      console.error('Complete tenant selection failed:', error);
      this.clearState();
      throw error;
    }
  }

  async logout(): Promise<void> {
    const refreshToken = this.state.refreshToken || localStorage.getItem('refreshToken');

    if (refreshToken) {
      try {
        await apiClient.post('/api/auth/logout', {
          refreshToken,
        });
      } catch {
        // Ignore logout errors
      }
    }

    this.clearState();

    const returnUrl = window.location.origin;
    const postLogoutRedirect = `${returnUrl}/login`;

    // If platform auth is active (e.g. SWA Easy Auth), route through
    // platform logout to clear its session cookie as well.
    try {
      const platformAuthProbe = await fetch('/.auth/me', {
        method: 'GET',
        credentials: 'include',
      });

      if (platformAuthProbe.ok || platformAuthProbe.status === 401 || platformAuthProbe.status === 403) {
        const platformLogoutUrl = `${returnUrl}/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirect)}`;
        window.location.href = platformLogoutUrl;
        return;
      }
    } catch {
      // Ignore probe errors and continue with app-level redirect.
    }

    window.location.href = postLogoutRedirect;
  }

  async refreshTokenIfNeeded(): Promise<void> {
    if (!this.state.token || !this.state.refreshToken) {
      throw new Error('No tokens available');
    }

    const sessionRevision = this.sessionRevision;
    try {
      const response = await apiClient.post('/api/auth/refresh', {
        refreshToken: this.state.refreshToken,
      }) as any;

      const profile = response.user ? null : await apiClient.get('/api/auth/me', {}, response.token) as any;
      if (sessionRevision !== this.sessionRevision) return;
      this.state = {
        ...this.state,
        token: response.token,
        refreshToken: response.refreshToken,
        user: response.user || profile.user || profile,
      };

      this.saveStateToStorage();
    } catch {
      if (sessionRevision !== this.sessionRevision) return;
      this.clearState();
      throw new Error('Token refresh failed');
    }
  }

  getAccessToken(): string | null {
    return this.state.token;
  }

  /**
   * Resolves the Entra OAuth authorize URL.
   * Prefers VITE_AUTH_URL if set; otherwise constructs from VITE_ENTRA_TENANT_ID.
   */
  private getEntraAuthorizeUrl(): string {
    const explicit = import.meta.env.VITE_AUTH_URL;
    if (explicit && !String(explicit).includes('<from-config>')) return `${explicit}/oauth2/v2.0/authorize`;
    const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID;
    if (!tenantId || String(tenantId).includes('<from-config>')) {
      throw new Error('Auth tenant configuration missing. Run Sync-PortalsEnvFromConfig.ps1 or set VITE_ENTRA_TENANT_ID.');
    }
    return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`;
  }

  /**
   * Resolves the client ID.
   * Prefers VITE_AUTH_CLIENT_ID; falls back to VITE_ENTRA_CLIENT_ID.
   */
  private getClientId(): string {
    const clientId = import.meta.env.VITE_AUTH_CLIENT_ID || import.meta.env.VITE_ENTRA_CLIENT_ID || '';
    if (!clientId || String(clientId).includes('<from-config>')) {
      throw new Error('Auth client configuration missing. Run Sync-PortalsEnvFromConfig.ps1 or set VITE_AUTH_CLIENT_ID.');
    }
    return clientId;
  }

  private async loginSuperAdmin(returnUrl: string): Promise<void> {
    const authUrl = this.getEntraAuthorizeUrl();
    const params = new URLSearchParams({
      client_id: this.getClientId(),
      response_type: 'code',
      redirect_uri: `${returnUrl}/login/complete`,
      scope: 'openid profile email',
      state: JSON.stringify({ portalType: 'super-admin', returnUrl }),
    });

    window.location.href = `${authUrl}?${params.toString()}`;
  }

  private async loginTenantUser(returnUrl: string): Promise<void> {
    const authUrl = this.getEntraAuthorizeUrl();
    const params = new URLSearchParams({
      client_id: this.getClientId(),
      response_type: 'code',
      redirect_uri: `${returnUrl}/login/complete`,
      scope: 'openid profile email',
      state: JSON.stringify({ 
        portalType: this.portalType,
        tenantSlug: this.tenantSlug,
        returnUrl,
      }),
      domain_hint: import.meta.env.VITE_AUTH_DOMAIN_HINT || '',
    });

    window.location.href = `${authUrl}?${params.toString()}`;
  }

  private async validateAndRefreshToken(token: string): Promise<void> {
    const sessionRevision = this.sessionRevision;
    try {
      const response = await apiClient.get('/api/auth/me', {}, token) as any;
      if (sessionRevision !== this.sessionRevision) return;
      
      this.state = {
        ...this.state,
        user: response.user,
        token,
        isAuthenticated: true,
      };
    } catch (firstErr) {
      if (this.state.refreshToken) {
        try {
          await this.refreshTokenIfNeeded();
        } catch (refreshErr) {
          throw refreshErr;
        }
        // Re-validate with the fresh token so that user is populated
        // and isAuthenticated is set to true.
        const freshToken = this.state.token;
        if (freshToken) {
          const response = await apiClient.get('/api/auth/me', {}, freshToken) as any;
          if (sessionRevision !== this.sessionRevision) return;
          this.state = {
            ...this.state,
            user: response.user,
            isAuthenticated: true,
          };
        }
      } else {
        throw new Error('Invalid token');
      }
    }
  }

  private getReturnUrl(): string {
    return window.location.origin;
  }

  private setupTokenRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }

    this.refreshTimer = setInterval(async () => {
      if (this.state.isAuthenticated && this.state.refreshToken) {
        try {
          await this.refreshTokenIfNeeded();
        } catch {
          // Token refresh failed, user will need to re-login
        }
      }
    }, 15 * 60 * 1000); // Refresh every 15 minutes
  }

  private saveStateToStorage(): void {
    const storageData = {
      token: this.state.token,
      refreshToken: this.state.refreshToken,
      user: this.state.user,
    };

    sessionStorage.setItem('auth_state', JSON.stringify(storageData));
    this.publish();
  }

  private loadStateFromStorage(): Partial<AuthState> {
    try {
      const stored = sessionStorage.getItem('auth_state');
      if (stored) {
        const data = JSON.parse(stored);
        return {
          token: data.token,
          refreshToken: data.refreshToken,
          user: data.user,
        };
      }
    } catch {
      // Ignore storage errors
    }
    return {};
  }

  private clearState(): void {
    this.sessionRevision += 1;
    this.state = {
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
    };
    sessionStorage.removeItem('auth_state');
    this.publish();
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    try {
      localStorage.removeItem(AUTH_METHOD_STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }

    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private persistAuthMethod(method: AuthMethod): void {
    try {
      localStorage.setItem(AUTH_METHOD_STORAGE_KEY, method);
    } catch {
      // Ignore storage errors
    }
  }
}

export const authService = new AuthService();
